/**
 * ShadcnDashboard – dark-first, polished SaaS-style dashboard.
 * Compact hero bar with inline KPI stats, recent people strip,
 * and category navigation with uniform block tiles in a grid.
 */

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
    Users, FolderOpen, Settings, Wrench, MapPin,
    X, Search, FileText,
    type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ShadcnRecentPeopleStrip } from './ShadcnRecentPeopleStrip';
import {
    dashboardItemMatchesFilter,
    isDashboardHash,
    isDashboardTileFilterActive,
    quadrantHasFilterMatch,
    buildPersonSearchHash,
} from './dashboardFilterUtils';
import type { QuadrantItemData } from './QuadrantItem';
import MeService from '../../services/MeService';
import { useEntityEntitlements } from '../../hooks/useEntityEntitlements';

// ─── Category Definitions ─────────────────────────────────────────────

interface CategoryConfig {
    title: string;
    icon: LucideIcon;
    accentColor: string;
    gradientFrom: string;
    gradientTo: string;
    badgeBg: string;
    badgeText: string;
}

const CATEGORIES: CategoryConfig[] = [
    {
        title: 'People',
        icon: Users,
        accentColor: '#3B82F6',
        gradientFrom: '#2563EB',
        gradientTo: '#3B82F6',
        badgeBg: 'rgba(37,99,235,0.15)',
        badgeText: '#3B82F6',
    },
    {
        title: 'Core & Places',
        icon: MapPin,
        accentColor: '#06B6D4',
        gradientFrom: '#06B6D4',
        gradientTo: '#22D3EE',
        badgeBg: 'rgba(6,182,212,0.15)',
        badgeText: '#06B6D4',
    },
    {
        title: 'Administration',
        icon: Settings,
        accentColor: '#8B5CF6',
        gradientFrom: '#8B5CF6',
        gradientTo: '#A855F7',
        badgeBg: 'rgba(139,92,246,0.15)',
        badgeText: '#8B5CF6',
    },
    {
        title: 'Tools & Meta',
        icon: Wrench,
        accentColor: '#A1A1AA',
        gradientFrom: '#71717A',
        gradientTo: '#A1A1AA',
        badgeBg: 'rgba(161,161,170,0.15)',
        badgeText: '#A1A1AA',
    },
];

const formatCount = (n: number): string => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 10_000) return `${(n / 1_000).toFixed(0)}k`;
    if (n >= 1_000) return n.toLocaleString();
    return n.toString();
};

// ─── Sub-components ────────────────────────────────────────────────────

function NavBlock({
    item,
    accentColor,
    dimmed,
}: {
    item: QuadrantItemData;
    accentColor: string;
    dimmed: boolean;
}) {
    const label = item.label.replace(/\u00A0\*/g, '').trim();

    return (
        <button
            onClick={item.onClick}
            className={cn(
                'group flex items-center gap-3 px-3.5 py-2.5 rounded-xl',
                'border border-white/[0.07] bg-white/[0.02]',
                'hover:border-white/[0.14] hover:bg-white/[0.06]',
                'transition-colors duration-150 cursor-pointer select-none',
                'min-w-0 w-full',
                dimmed && 'opacity-20 grayscale pointer-events-none',
            )}
            style={{
                ['--nav-accent' as string]: accentColor,
            }}
            title={item.count != null ? `${label} (${item.count.toLocaleString()})` : label}
        >
            <span
                className="w-1.5 h-6 rounded-full shrink-0"
                style={{ backgroundColor: accentColor }}
            />
            <span className="text-sm font-medium text-zinc-300 flex-1 text-left leading-snug">
                {label}
            </span>
            {item.countLoading ? (
                <span className="w-3 h-3 rounded-full bg-zinc-600 animate-pulse shrink-0" />
            ) : item.count != null ? (
                <span
                    className="font-mono text-[11px] font-semibold rounded-md px-1.5 py-0.5 shrink-0"
                    style={{
                        backgroundColor: `${accentColor}18`,
                        color: accentColor,
                    }}
                >
                    {formatCount(item.count)}
                </span>
            ) : null}
        </button>
    );
}

function CategoryCard({
    config,
    items,
    filterQuery,
}: {
    config: CategoryConfig;
    items: QuadrantItemData[];
    filterQuery: string;
}) {
    const tileFilterActive = isDashboardTileFilterActive(filterQuery);
    const hasMatch = quadrantHasFilterMatch(items, filterQuery);
    const collapsed = tileFilterActive && !hasMatch;

    return (
        <div
            className={cn(
                'flex rounded-2xl border border-white/[0.06] overflow-hidden bg-white/[0.02]',
                'transition-all duration-200',
                collapsed && 'opacity-30 max-h-14',
            )}
        >
            <div
                className="w-1.5 shrink-0"
                style={{
                    background: `linear-gradient(180deg, ${config.gradientFrom} 0%, ${config.gradientTo} 100%)`,
                }}
            />
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-white/[0.05]">
                    <config.icon size={16} color={config.accentColor} />
                    <span className="text-[14px] font-semibold text-zinc-200">
                        {config.title}
                    </span>
                    <span
                        className="font-mono text-[11px] font-medium rounded-md px-1.5 py-0.5"
                        style={{
                            backgroundColor: config.badgeBg,
                            color: config.badgeText,
                        }}
                    >
                        {items.length}
                    </span>
                </div>
                {!collapsed && (
                    <div
                        className="grid gap-2 p-3"
                        style={{
                            gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                        }}
                    >
                        {items.map((item) => (
                            <NavBlock
                                key={item.id}
                                item={item}
                                accentColor={item.color || config.accentColor}
                                dimmed={tileFilterActive && !dashboardItemMatchesFilter(item, filterQuery)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function StatChip({
    label,
    value,
    loading,
    accent,
    glowing,
    onClick,
}: {
    label: string;
    value: string | number;
    loading?: boolean;
    accent?: boolean;
    glowing?: boolean;
    onClick?: () => void;
}) {
    const Wrapper = onClick ? 'button' : 'div';

    return (
        <Wrapper
            onClick={onClick}
            className={cn(
                'flex items-center gap-2.5 rounded-lg border px-4 py-2',
                accent
                    ? 'border-orange-500/20 bg-orange-500/[0.06] hover:bg-orange-500/[0.10]'
                    : 'border-white/[0.06] bg-white/[0.03]',
                onClick && 'cursor-pointer transition-colors duration-150',
            )}
        >
            {glowing && (
                <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse shrink-0" />
            )}
            <span className={cn('text-[11px] font-semibold uppercase tracking-wide', accent ? 'text-orange-400/70' : 'text-white/30')}>
                {label}
            </span>
            {loading ? (
                <span className="w-8 h-5 rounded bg-white/[0.06] animate-pulse" />
            ) : (
                <span className={cn('text-lg font-bold tracking-tight leading-none', accent ? 'text-orange-400' : 'text-zinc-100')}>
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </span>
            )}
        </Wrapper>
    );
}

// ─── Main Component ────────────────────────────────────────────────────

interface ShadcnDashboardProps {
    items: QuadrantItemData[];
    onNavigateHash?: (hash: string) => void;
    onNavigate?: (destination: string, isEntity?: boolean, category?: 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin') => void;
    active?: boolean;
}

export const ShadcnDashboard: React.FC<ShadcnDashboardProps> = ({
    items,
    onNavigateHash,
    onNavigate,
    active = true,
}) => {
    const [filterQuery, setFilterQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const hasAnimated = useRef(false);
    const { entityEntitlements } = useEntityEntitlements();

    const [meData, setMeData] = useState(MeService.getMe());
    useEffect(() => {
        const update = () => setMeData(MeService.getMe());
        update();
        return MeService.subscribe(update);
    }, []);

    useEffect(() => {
        if (items.length > 0) hasAnimated.current = true;
    }, [items]);

    useEffect(() => {
        const clear = () => { if (isDashboardHash()) setFilterQuery(''); };
        clear();
        window.addEventListener('hashchange', clear);
        return () => window.removeEventListener('hashchange', clear);
    }, []);

    useEffect(() => {
        if (active) setFilterQuery('');
    }, [active]);

    const groups = useMemo(() => {
        const g: QuadrantItemData[][] = [[], [], [], []];
        items.forEach((item) => {
            const q = Math.min(item.quadrant, 3);
            g[q].push(item);
        });
        return g;
    }, [items]);

    const pendingTotal = useMemo(() => {
        const summary = entityEntitlements?.RequestApprovalTypeSummary ?? [];
        return summary.reduce((sum, a) => sum + (a.Counts?.Pending ?? 0), 0);
    }, [entityEntitlements]);

    const personCount = useMemo(() => {
        const allUsers = items.find((i) => i.id === 'allUsers');
        return allUsers?.count ?? null;
    }, [items]);

    const groupCount = useMemo(() => {
        const grp = items.find((i) => i.id === 'groups');
        return grp?.count ?? null;
    }, [items]);

    const contentCount = useMemo(() => {
        const c = items.find((i) => i.id.toLowerCase().includes('content') && !i.id.includes('util'));
        return c?.count ?? null;
    }, [items]);

    const countsLoading = items.length > 0 && items[0]?.countLoading;

    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    }, []);

    const firstName = useMemo(() => {
        const dn = meData?.DisplayName ?? '';
        if (!dn) return '';
        return dn.split(/\s+/)[0];
    }, [meData]);

    const totalIdentities = useMemo(() => {
        let total = 0;
        items.forEach((item) => {
            if (item.quadrant === 0 && item.id === 'allUsers' && item.count != null) {
                total = item.count;
            }
        });
        return total;
    }, [items]);

    const entityTypeCount = useMemo(() => {
        const types = new Set<string>();
        items.forEach((item) => {
            if (item.quadrant === 0 && item.id !== 'allUsers') {
                types.add(item.id);
            }
        });
        return types.size;
    }, [items]);

    const handleFilterKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const q = filterQuery.trim();
            if (!active || !onNavigateHash || !q.includes(',') || !isDashboardHash()) return;
            const hash = buildPersonSearchHash(q);
            setFilterQuery('');
            onNavigateHash(hash);
        },
        [active, filterQuery, onNavigateHash],
    );

    const handlePendingClick = useCallback(() => {
        if (onNavigate) onNavigate('Requestapproval', true, 'other');
    }, [onNavigate]);

    const skipAnim = hasAnimated.current;

    return (
        <div className={cn('flex flex-col gap-3 pb-6', !skipAnim && 'animate-in fade-in duration-500')}>
            {/* ─── Compact Hero Bar ─────────────────────────────────── */}
            <div
                className="mx-6 mt-3 rounded-xl relative overflow-hidden border border-white/[0.06] px-6 py-4"
                style={{
                    background: 'linear-gradient(135deg, #18181B 0%, #1E293B 50%, #0F172A 100%)',
                }}
            >
                <div className="absolute -top-6 -right-3 w-[160px] h-[160px] rounded-full bg-blue-600/[0.06] pointer-events-none" />

                <div className="relative flex items-center gap-6">
                    {/* Greeting */}
                    <div className="flex flex-col gap-0.5 shrink-0">
                        <h1 className="text-xl font-bold text-zinc-50 tracking-tight">
                            {greeting}{firstName ? `, ${firstName}` : ''}
                        </h1>
                        <p className="text-[13px] text-white/40">
                            {totalIdentities > 0
                                ? `Managing ${totalIdentities.toLocaleString()} identities \u00B7 ${entityTypeCount} types`
                                : 'Loading platform...'}
                        </p>
                    </div>

                    {/* Stat chips inline */}
                    <div className="flex items-center gap-2 flex-1 justify-end">
                        <StatChip label="Users" value={personCount ?? 0} loading={countsLoading} />
                        <StatChip label="Groups" value={groupCount ?? 0} loading={countsLoading} />
                        <StatChip label="Content" value={contentCount ?? 0} loading={countsLoading} />
                        {pendingTotal > 0 && (
                            <StatChip
                                label="Pending"
                                value={pendingTotal}
                                accent
                                glowing
                                onClick={handlePendingClick}
                            />
                        )}

                        {/* Search */}
                        <div className="flex items-center gap-1.5 py-2 px-3 rounded-lg bg-white/[0.05] border border-white/[0.07] ml-2">
                            <Search size={14} className="text-white/35" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={filterQuery}
                                onChange={(e) => setFilterQuery(e.target.value)}
                                onKeyDown={handleFilterKeyDown}
                                placeholder="Filter..."
                                className="bg-transparent border-none outline-none text-[13px] text-white/60 placeholder:text-white/25 w-20 focus:w-36 transition-all duration-200"
                                aria-label="Filter dashboard"
                            />
                            {filterQuery && (
                                <button onClick={() => setFilterQuery('')} className="text-white/30 hover:text-white/60">
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Recent People Strip ────────────────────────────────── */}
            <ShadcnRecentPeopleStrip onNavigateHash={onNavigateHash} />

            {/* ─── Category Navigation Grid ───────────────────────────── */}
            <div className="grid grid-cols-2 gap-3 px-6">
                {CATEGORIES.map((cat, i) => (
                    <CategoryCard
                        key={cat.title}
                        config={cat}
                        items={groups[i]}
                        filterQuery={filterQuery}
                    />
                ))}
            </div>
        </div>
    );
};
